import { useState } from 'react';
import { Tooltip, assets } from '@round/whitelabel-ui';
import capitalize from 'lodash/capitalize';
import styles from './PlatformStatsBadge.module.css';
import { SkeletonizedValue } from '@round/ui-kit';
import { asMoney, formatNumberToKNotation } from 'helpers';
import cn from 'classnames';
import { toDecimalPoint } from '@round/utils';
import { useNavigate } from 'react-router-dom';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { useAggregatedPostStats } from '@round/creatorbase-helpers/api-hooks';

const {
    TiktokSolid: TikTokIcon,
    InstagramSolid: InstagramIcon,
    YoutubeSolid: YoutubeIcon,
    TiktokColor: TikTokActiveIcon,
    InstagramColor: InstagramActiveIcon,
    YoutubeColor: YoutubeActiveIcon,
} = assets;

type Props = {
    projectId: number;
    platform: string;
    isActive: boolean;
};

const platformIcons = {
    tiktok: TikTokIcon,
    instagram: InstagramIcon,
    youtube: YoutubeIcon,
};

const activePlatformIcons = {
    tiktok: TikTokActiveIcon,
    instagram: InstagramActiveIcon,
    youtube: YoutubeActiveIcon,
};

const PlatformStatsBadge = ({ projectId, platform, isActive }: Props) => {
    const { getCurrencyById } = useCurrencies();
    const navigate = useNavigate();

    const [anchorElement, setAnchorElement] = useState<SVGSVGElement | null>(null);
    const [delay, setDelay] = useState<ReturnType<typeof setTimeout> | null>(null);
    const handleWithDelay = (callback: () => void) => {
        setDelay(setTimeout(() => callback(), 300));
    };

    const [shouldFetch, setShouldFetch] = useState(false);

    const { data, status } = useAggregatedPostStats(
        { project_id: projectId, platform },
        { enabled: isActive && shouldFetch }
    );

    const isInitialized = status === 'success' || status === 'error';

    const PlatformIcon = isActive
        ? activePlatformIcons[platform as keyof typeof activePlatformIcons]
        : platformIcons[platform as keyof typeof platformIcons];

    const formatValuesMappedByCurrency = (data: Record<string, number>) =>
        Object.entries(data).map(([curr, value]) => asMoney(value, getCurrencyById(Number(curr))));

    const formattedBudgetValues = formatValuesMappedByCurrency(data?.total_budget || {});
    const formattedCpmValues = formatValuesMappedByCurrency(data?.cpm || {});
    const engagementRate =
        typeof data?.engagement_rate === 'number' ? `${toDecimalPoint(data.engagement_rate * 100, 2)}%` : '-';
    const formattedViewCount = typeof data?.view_count === 'number' ? formatNumberToKNotation(data?.view_count) : '-';

    if (!isActive) {
        return (
            <PlatformIcon
                role="img"
                title={`Inactive ${platform}`}
                className={cn(styles.platformIcon, styles.ghostIcon)}
            />
        );
    }

    return (
        <>
            <PlatformIcon
                role="img"
                title={platform}
                onClick={(e) => {
                    if (!isActive) {
                        return;
                    }

                    e.stopPropagation();
                    navigate(`${projectId}/#${platform}`);
                }}
                className={styles.platformIcon}
                // To prevent fetching data on quick mouse-overs
                onMouseEnter={() => handleWithDelay(() => setShouldFetch(true))}
                onMouseLeave={() => (delay ? clearTimeout(delay) : undefined)}
                ref={setAnchorElement}
            />
            <Tooltip anchorElement={anchorElement} className={styles.tooltip}>
                <Tooltip.Title>{capitalize(platform)}</Tooltip.Title>
                <Tooltip.Body>
                    {status === 'error' ? (
                        <>Something went wrong</>
                    ) : (
                        <dl className={styles.stats}>
                            <div className={styles.item}>
                                <dt>Posts</dt>

                                <SkeletonizedValue
                                    className={styles.skeleton}
                                    containerClassName={styles.skeletonContainer}
                                    isInitialized={isInitialized}
                                >
                                    <dd>{data?.post_count ?? '-'}</dd>
                                </SkeletonizedValue>
                            </div>

                            <div className={styles.item}>
                                <dt>Total budget</dt>

                                <SkeletonizedValue
                                    className={styles.skeleton}
                                    containerClassName={styles.skeletonContainer}
                                    isInitialized={isInitialized}
                                >
                                    <dd>
                                        {formattedBudgetValues.length
                                            ? formattedBudgetValues.map((value, index) => (
                                                  <div key={index}>{value}</div>
                                              ))
                                            : '-'}
                                    </dd>
                                </SkeletonizedValue>
                            </div>

                            <div className={styles.item}>
                                <dt>Total views</dt>

                                <SkeletonizedValue
                                    isInitialized={isInitialized}
                                    className={styles.skeleton}
                                    containerClassName={styles.skeletonContainer}
                                >
                                    <dd>{formattedViewCount}</dd>
                                </SkeletonizedValue>
                            </div>

                            <div className={styles.item}>
                                <dt>CPM</dt>

                                <SkeletonizedValue
                                    className={styles.skeleton}
                                    containerClassName={styles.skeletonContainer}
                                    isInitialized={isInitialized}
                                >
                                    <dd>
                                        {formattedCpmValues.length
                                            ? formattedCpmValues.map((value, index) => <div key={index}>{value}</div>)
                                            : '-'}
                                    </dd>
                                </SkeletonizedValue>
                            </div>

                            <div className={styles.item}>
                                <dt>Engagement rate</dt>

                                <SkeletonizedValue
                                    className={styles.skeleton}
                                    containerClassName={styles.skeletonContainer}
                                    isInitialized={isInitialized}
                                >
                                    <dd>{engagementRate}</dd>
                                </SkeletonizedValue>
                            </div>
                        </dl>
                    )}
                </Tooltip.Body>
            </Tooltip>
        </>
    );
};

export default PlatformStatsBadge;
